import styles from './CreateSIZ.module.css';
import Button from '../../Button/Button';
import { getAllDocumentsUrl, getAllMaterialsUrl } from '../../../helpers/constants';
import { useEffect, useMemo, useReducer, useState } from 'react';
import InputForm from '../../InputForm/InputForm';
import { SelectForm } from '../../SelectForm/SelectForm';
import { formReducer, INITIAL_STATE } from './CreateSIZ.state';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import useApi from '../../../hooks/useApi.hook';
import { useUserDetails } from '../../../hooks/useUserDetails.hook';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import {notifyDownloadQueued} from '../../../helpers/downloadsEvents';
import CreateSIZRightPanel from './RightPanel/CreateSIZRightPanel';

export function CreateSIZ({
	optionsUser,
	userDict,
	loadingUsers,
	setCreateModalOpen,
	setRefreshKey,
	currentSIZ,
	typesDict
}) {
	const isEdit = Boolean(currentSIZ);
	const api = useApi();
	const [error, setError] = useState(undefined);
	const [formState, dispatchForm] = useReducer(formReducer, INITIAL_STATE);
	const { values, isValid, isFormReadyToSubmit } = formState;
	const { userDetails, setUserDetails, loadingUser, fetchUserDetails } = useUserDetails();
	const [addedItems, setAddedItems] = useState([]);
	const fio = (u) => [u.last_name, u.name, u.father_name].filter(Boolean).join(' ');

	const manageSIZ = async (payload) => {
		try {
			await api.patch(`${getAllMaterialsUrl}${currentSIZ.id}`, payload);
			setCreateModalOpen(false);
			setRefreshKey((prev) => prev + 1);
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
		}
	};

	const submitBulk = async () => {
		if (!addedItems.length || !values.user_id) return;
		const payload = {
			user_id: values.user_id,
			number_of_document: values.number_of_document || null,
			materials_data: addedItems.map(item => ({
				material_type_id: item.material_type_id,
				sertificate: item.sertificate || null,
				start_date: values.start_date || null,
				period: item.period,
				quantity: item.quantity,
				brand: item.brand || null
			}))
		};
		try {
			await api.post('/materials/bulk', payload);
			setCreateModalOpen(false);
			setRefreshKey((prev) => prev + 1);
		} catch (e) {
			setError(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
			return;
		}
		try {
			const response = await api.post(`${getAllDocumentsUrl}/issue_siz_generate`, payload);
			notifyDownloadQueued(response.data);
		} catch (e) {
			console.error('Ошибка при загрузке документа:', e);
		}
	};

	useEffect(() => {
		if (currentSIZ) {
			dispatchForm({ type: 'SET_FROM_SIZ', payload: currentSIZ });
			if (currentSIZ.user_id) {
				fetchUserDetails(currentSIZ.user_id);
			}
		}
	}, [currentSIZ]);

	const normTypeOptions = useMemo(() => {
		const normTypes = userDetails?.profession?.norm?.material_norm_types;
		if (!normTypes?.length) return null;
		const actualQty = {};
		for (const mat of (userDetails.materials || [])) {
			const tid = mat.material_type.id;
			actualQty[tid] = (actualQty[tid] || 0) + (mat.quantity || 0);
		}
		return normTypes.map(nmt => ({
			value: String(nmt.material_type.id),
			label: `${actualQty[nmt.material_type.id] || 0}/${nmt.quantity} — ${nmt.material_type.title}`
		}));
	}, [userDetails]);

	const typeOptions = normTypeOptions || [];

	const selectedUser = useMemo(() => {
		const id = values.user_id == null ? null : String(values.user_id);
		if (!id) return null;
		return (
			optionsUser.find(o => o.value === id) ||
			(userDict[id] ? { value: id, label: fio(userDict[id]) } : null)
		);
	}, [values.user_id, optionsUser, userDict]);

	const selectedType = useMemo(() => {
		const id = values.material_type_id == null ? null : String(values.material_type_id);
		if (!id) return null;
		return typeOptions.find(o => o.value === id) || null;
	}, [values.material_type_id, typeOptions]);

	// edit mode only
	const brandOptions = (typesDict[values.material_type_id]?.brands || []).map(b => ({ value: b, label: b }));
	const selectedBrand = brandOptions.find(o => o.value === values.brand) || null;

	const onChange = (e) => {
		dispatchForm({ type: 'SET_VALUE', payload: { [e.target.name]: e.target.value } });
		dispatchForm({ type: 'SET_VALIDITY_FOR_FIELD', payload: e.target.name });
	};

	const selectUser = (e) => {
		dispatchForm({ type: 'SET_VALUE', payload: { user_id: e.value, material_type_id: null } });
		dispatchForm({ type: 'SET_VALIDITY_FOR_FIELD', payload: 'user_id' });
		setUserDetails(null);
		setAddedItems([]);
		fetchUserDetails(e.value);
	};

	const selectType = (e) => {
		dispatchForm({ type: 'SET_VALUE', payload: { material_type_id: e.value, brand: null } });
		dispatchForm({ type: 'SET_VALIDITY_FOR_FIELD', payload: 'material_type_id' });
	};

	const addItem = () => {
		if (!values.material_type_id) return;
		const normTypes = userDetails?.profession?.norm?.material_norm_types || [];
		const nmt = normTypes.find(n => String(n.material_type.id) === String(values.material_type_id));
		setAddedItems(prev => [...prev, {
			id: Date.now(),
			material_type_id: Number(values.material_type_id),
			title: nmt?.material_type?.title || '',
			quantity: nmt?.quantity ?? 1,
			period: nmt?.period ?? 365,
			sertificate: '',
			brand: null
		}]);
		dispatchForm({ type: 'SET_VALUE', payload: { material_type_id: null } });
	};

	const setNumber = (e, { min = 1, integer = true } = {}) => {
		const name = e?.target?.name;
		const raw = typeof e === 'number'
			? e
			: e?.target?.valueAsNumber ?? Number(e?.target?.value ?? e?.value ?? e);
		let num = Number.isFinite(raw) ? raw : NaN;
		if (!Number.isFinite(num)) num = min;
		if (integer) num = Math.floor(num);
		if (num < min) num = min;
		dispatchForm({ type: 'SET_VALUE', payload: { [name]: num } });
		dispatchForm({ type: 'SET_VALIDITY_FOR_FIELD', payload: name });
	};

	const normalizeNumber = (e, { min = 1, integer = true } = {}) => {
		const name = e?.target?.name;
		let current = Number(values[name]);
		if (!Number.isFinite(current)) current = min;
		if (integer) current = Math.floor(current);
		if (current < min) current = min;
		dispatchForm({ type: 'SET_VALUE', payload: { [name]: current } });
	};

	const setStartDate = (e) => {
		const raw = e?.target?.value ?? e?.value ?? e;
		const val = (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw))
			? raw
			: new Date().toISOString().slice(0, 10);
		dispatchForm({ type: 'SET_VALUE', payload: { start_date: val } });
		dispatchForm({ type: 'SET_VALIDITY_FOR_FIELD', payload: 'start_date' });
	};

	const normalizeStartDate = (e) => {
		if (!e?.target?.value) setStartDate('');
	};

	const addSIZ = (e) => {
		e.preventDefault();
		dispatchForm({ type: 'SUBMIT' });
	};

	useEffect(() => {
		if (isFormReadyToSubmit) {
			const payload = {
				...values,
				material_type_id: values.material_type_id != null ? Number(values.material_type_id) : null
			};
			manageSIZ(payload);
			dispatchForm({ type: 'CLEAR' });
		}
	}, [isFormReadyToSubmit, values, manageSIZ]);

	const errorMessage = 'Обязательное поле';

	return (
		<div className={`${styles.create_user} ${isEdit ? styles.create_user_edit : ''}`}>
			<h1 className={styles.title}>{isEdit ? 'Редактировать' : 'Выдать материлы'}</h1>
			<ErrorMessage error={error} onClose={() => setError(undefined)} />
			<form onSubmit={addSIZ} className={styles.form}>
				<div className={`${styles.content} ${isEdit ? styles.content_edit : ''}`}>
					<div className={styles.left_panel}>
						<span className={styles.span} data-tooltip-id="errorTooltipUser">
							Сотрудник*:
							<SelectForm
								value={selectedUser}
								options={optionsUser}
								placeholder={loadingUsers ? 'Загрузка...' : 'Работник'}
								name="user_id"
								onChange={selectUser}
								isValid={isValid.user_id}
								isDisabled={isEdit || loadingUsers}
							/>
							<Tooltip
								id="errorTooltipUser"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.user_id}
								className={styles['my-tooltip']}
							/>
						</span>
						<span className={styles.span} data-tooltip-id="errorTooltipType">
							Тип СИЗ*:
							<SelectForm
								value={selectedType}
								options={typeOptions}
								placeholder={loadingUser ? 'Загрузка...' : 'Тип материала'}
								name="material_type_id"
								onChange={selectType}
								isValid={isValid.material_type_id}
								isDisabled={isEdit || loadingUser}
							/>
							<Tooltip
								id="errorTooltipType"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.material_type_id}
								className={styles['my-tooltip']}
							/>
						</span>

						{isEdit && <>
							<span className={styles.span}>
								Марка СИЗ:
								<SelectForm
									value={selectedBrand}
									options={brandOptions}
									placeholder="Выберите бренд"
									name="brand"
									onChange={e => dispatchForm({ type: 'SET_VALUE', payload: { brand: e ? e.value : null } })}
									isClearable
									isDisabled={!values.material_type_id}
								/>
							</span>
							<span className={styles.span}>
								Количество*, шт.:
								<InputForm value={values.quantity} type="number" name="quantity"
									min={1} step={1} placeholder="Количество"
									onChange={setNumber} onBlur={normalizeNumber} />
							</span>
							<span className={styles.span}>
								Дата выдачи*:
								<InputForm value={values.start_date} type="date" name="start_date"
									placeholder="Дата выдачи"
									onChange={setStartDate} onBlur={normalizeStartDate} />
							</span>
							<span className={styles.span}>
								Период поверки, дней:
								<InputForm value={values.period} type="number" name="period"
									min={1} step={1} placeholder="Период поверки"
									onChange={setNumber} onBlur={normalizeNumber} />
							</span>
							<span className={styles.span}>
								Сертификат:
								<InputForm value={values.sertificate || ''} type="text" name="sertificate"
									placeholder="Сертификат" onChange={onChange} />
							</span>
							<span className={styles.span}>
								Номер документа:
								<InputForm value={values.number_of_document || ''} type="text" name="number_of_document"
									placeholder="Номер документа" onChange={onChange} />
							</span>
						</>}

						<div className={styles.button}>
							{isEdit
								? <Button>Редактировать</Button>
								: <Button type="button" onClick={addItem} disabled={!values.material_type_id}>
									Добавить к выдаче
								</Button>
							}
						</div>
					</div>

					{!isEdit && (
						<CreateSIZRightPanel
							items={addedItems}
							typesDict={typesDict}
							onItemChange={(idx, updated) =>
								setAddedItems(prev => prev.map((it, i) => i === idx ? updated : it))
							}
							onItemRemove={(idx) =>
								setAddedItems(prev => prev.filter((_, i) => i !== idx))
							}
							startDate={values.start_date}
							onStartDateChange={setStartDate}
							onStartDateBlur={normalizeStartDate}
							numberOfDocument={values.number_of_document}
							onNumberOfDocumentChange={onChange}
							onSubmit={submitBulk}
							submitDisabled={!addedItems.length || !values.user_id}
						/>
					)}
				</div>
			</form>
		</div>
	);
}